/**
 * Stage 1 — Folder pick.
 *
 * Three sub-states based on what's set:
 *   no app-config        → choose app-config folder
 *   no work folder       → choose work folder
 *   both set             → show queue + extract button
 */

import { getState, setState } from '../state.js';
import { el, clear, toast } from './dom.js';
import {
  pickAppConfigFolder,
  pickWorkFolder,
  ensureReadable,
  enumeratePdfs,
  formatBytes
} from '../folders.js';
import { renderThumbnail, getPageCount } from '../pdf.js';
import { renderApiKeyPanel } from './api-key-panel.js';

let mounted = null;

export function renderFolderStage(mount) {
  clear(mount);
  mounted = mount;

  const root = el('div');

  // API key panel always shows on this stage
  root.appendChild(renderApiKeyPanel(() => renderFolderStage(mounted)));

  const state = getState();
  if (!state.appConfigHandle) {
    root.appendChild(renderAppConfigEmpty());
  } else if (!state.workHandle) {
    root.appendChild(renderWorkEmpty());
  } else {
    root.appendChild(renderQueue(state));
  }

  mount.appendChild(root);
}

function renderAppConfigEmpty() {
  return el('div', { class: 'panel panel-empty' }, [
    el('h2', {}, ['Choose your ', el('em', {}, ['app-config']), ' folder']),
    el('p', {}, [
      'One-time setup. WiseEntry keeps its config — your CSV template (',
      el('code', { style: { fontFamily: 'var(--font-mono)' } }, ['wiseentry.csv']),
      ') and rules backups — in this folder. Picked once, then remembered.'
    ]),
    el('div', { class: 'actions' }, [
      el('button', {
        class: 'primary-btn',
        onClick: async () => {
          try {
            const handle = await pickAppConfigFolder();
            setState({ appConfigHandle: handle });
            toast('App-config folder set.', 'success');
            renderFolderStage(mounted);
          } catch (err) {
            if (err.name !== 'AbortError') toast(`Could not pick folder: ${err.message}`, 'error');
          }
        }
      }, ['Pick app-config folder'])
    ])
  ]);
}

function renderWorkEmpty() {
  return el('div', { class: 'panel panel-empty' }, [
    el('h2', {}, ['Pick today\u2019s ', el('em', {}, ['work']), ' folder']),
    el('p', {}, [
      'Choose the folder containing your scanned invoice PDFs. WiseEntry reads PDFs from it and writes a CSV back to the same folder. Originals are never modified.'
    ]),
    el('div', { class: 'actions' }, [
      el('button', {
        class: 'primary-btn',
        onClick: () => loadWorkFolder()
      }, ['Pick work folder']),
      el('button', {
        class: 'ghost-btn',
        onClick: async () => {
          try {
            const handle = await pickAppConfigFolder();
            setState({ appConfigHandle: handle });
            toast('App-config folder updated.', 'success');
            renderFolderStage(mounted);
          } catch (err) {
            if (err.name !== 'AbortError') toast(err.message, 'error');
          }
        }
      }, ['Change app-config folder'])
    ])
  ]);
}

async function loadWorkFolder() {
  try {
    const handle = await pickWorkFolder();
    setState({ workHandle: handle, workName: handle.name, files: [] });
    renderFolderStage(mounted);
    await scanFolder();
  } catch (err) {
    if (err.name !== 'AbortError') toast(`Could not pick folder: ${err.message}`, 'error');
  }
}

async function scanFolder() {
  const state = getState();
  if (!state.workHandle) return;

  if (!(await ensureReadable(state.workHandle))) {
    toast('Permission denied for work folder.', 'error');
    return;
  }

  let entries;
  try {
    entries = await enumeratePdfs(state.workHandle);
  } catch (err) {
    toast(`Failed to read folder: ${err.message}`, 'error');
    return;
  }

  if (entries.length === 0) {
    toast('No PDFs found in this folder.', 'error');
    setState({ files: [] });
    renderFolderStage(mounted);
    return;
  }

  const files = entries.map((e, i) => ({
    id: `f${i}-${e.name}`,
    name: e.name,
    size: e.size,
    handle: e.handle,
    file: e.file,
    status: 'pending',
    thumbDataUrl: null,
    pageCount: null,
    extracted: null,
    coding: null,
    distribution: null,
    distributionEdits: null,
    error: null,
    ms: null,
    edits: null
  }));

  setState({ files });
  renderFolderStage(mounted);

  // Generate thumbnails in the background
  for (const f of files) {
    try {
      const [thumb, pages] = await Promise.all([
        renderThumbnail(f.file).catch(() => null),
        getPageCount(f.file).catch(() => null)
      ]);
      f.thumbDataUrl = thumb;
      f.pageCount = pages;
      const row = mounted.querySelector(`[data-file-id="${f.id}"]`);
      if (row) {
        const thumbBox = row.querySelector('.thumb');
        if (thumb && thumbBox) {
          clear(thumbBox);
          thumbBox.appendChild(el('img', { src: thumb, alt: '' }));
        }
        const meta = row.querySelector('.fmeta');
        if (meta) meta.textContent = `${formatBytes(f.size)}${pages ? ` · ${pages} page${pages === 1 ? '' : 's'}` : ''}`;
      }
    } catch { /* per-file thumb failures are non-fatal */ }
  }
}

function renderQueue(state) {
  const wrap = el('div');

  wrap.appendChild(el('div', { class: 'path-bar' }, [
    el('div', {}, [
      el('span', { class: 'label' }, ['Work folder']),
      el('span', { class: 'path' }, [state.workName || '(unknown)']),
      el('span', { class: 'meta' }, [` · ${state.files.length} PDF${state.files.length === 1 ? '' : 's'}`])
    ]),
    el('div', { class: 'actions' }, [
      el('button', { class: 'ghost-btn', onClick: () => scanFolder() }, ['Refresh']),
      el('button', {
        class: 'ghost-btn',
        onClick: () => {
          setState({ workHandle: null, workName: null, files: [] });
          renderFolderStage(mounted);
        }
      }, ['Change folder'])
    ])
  ]));

  const list = el('div', { class: 'queue-list' });
  for (const f of state.files) list.appendChild(renderQueueRow(f));
  wrap.appendChild(list);

  const hasKey = state.hasKey();
  wrap.appendChild(el('div', { class: 'batch-summary' }, [
    el('div', { class: 'summary-counts' }, [
      el('div', { class: 'count' }, [
        el('span', { class: 'num' }, [String(state.files.length)]),
        el('span', { class: 'lbl' }, ['in queue'])
      ])
    ]),
    el('div', { class: 'summary-actions' }, [
      el('button', {
        class: 'primary-btn',
        disabled: !hasKey || state.files.length === 0,
        onClick: () => setState({ stage: 'extract' })
      }, [hasKey ? 'Extract all →' : 'Add API key first'])
    ])
  ]));

  return wrap;
}

function renderQueueRow(f) {
  const meta = `${formatBytes(f.size)}${f.pageCount ? ` · ${f.pageCount} page${f.pageCount === 1 ? '' : 's'}` : ''}`;
  const thumbContent = f.thumbDataUrl
    ? el('img', { src: f.thumbDataUrl, alt: '' })
    : document.createTextNode('PDF');

  return el('div', { class: 'queue-row', 'data-file-id': f.id }, [
    el('div', { class: 'thumb' }, [thumbContent]),
    el('div', { style: { minWidth: '0' } }, [
      el('div', { class: 'fname', title: f.name }, [f.name]),
      el('div', { class: 'fmeta' }, [meta])
    ]),
    el('span', { class: 'badge pending' }, [el('span', { class: 'dot' }), 'Pending']),
    el('div', { class: 'timing' }, []),
    el('button', {
      class: 'icon-btn',
      title: 'Remove from queue',
      onClick: () => {
        const state = getState();
        setState({ files: state.files.filter(x => x.id !== f.id) });
        renderFolderStage(mounted);
      }
    }, ['×'])
  ]);
}
